import Head from "next/head"
import { useRouter } from "next/router"
import { useCallback, useMemo } from "react"
import { match } from "ts-pattern"

import { useOrgSettingsDetailQuery } from "@/v2/graphql/public-generated-rq"
import { OrgSettingsDetailQuery } from "@/v2/graphql/public-generated-rq"
import { Tabs, Text } from "@lumen-energy/design-system"
import { UseQueryResult } from "@tanstack/react-query"

import { Loader } from "../Loader"
import { PageContainer } from "../layout/app-layout/AppLayout"
import { OrgSettings } from "./OrgSettings"
import { UserManagement } from "./user-management/UserManagement"

export const SettingsLoader = () => {
  const result = useOrgSettingsDetailQuery()
  return (
    <Loader
      result={result}
      render={({ data, refetch }) => {
        const { currentUser, portfolios } = data
        const org = currentUser?.org
        return <Settings {...{ org, portfolios, refetch }} />
      }}
    />
  )
}

export enum SettingsTab {
  Financial = "Financial",
  Users = "Users",
}

const maybeTab = (tab: any): SettingsTab | undefined => {
  if (typeof tab === "string" && tab in SettingsTab) return tab as SettingsTab
  return undefined
}

const Settings = ({
  org,
  portfolios,
  refetch,
}: {
  org: OrgSettingsDetailQuery["currentUser"]["org"]
  portfolios: OrgSettingsDetailQuery["portfolios"]
  refetch: () => Promise<UseQueryResult<OrgSettingsDetailQuery, unknown>>
}) => {
  const isFinancier = org?.isFinancier
  const { query, push } = useRouter()

  const tab: SettingsTab = useMemo(() => {
    const _default = isFinancier ? SettingsTab.Financial : SettingsTab.Users
    const tab = maybeTab(query.tab)
    return tab && tab in SettingsTab ? tab : _default
  }, [isFinancier, query.tab])

  const onChangeTab = useCallback(
    (tab: string) => push({ query: { ...query, tab } }),
    [push, query],
  )

  return (
    <>
      <Head>
        <title>Settings - Lumen Energy</title>
      </Head>
      <div className="flex flex-col items-center">
        <div className="w-full border-b border-solid border-grey-300 bg-white">
          <div className="mx-auto w-full max-w-screen-2xl p-5 pb-0">
            <div className="mb-4">
              <Text.H5>Settings</Text.H5>
              {isFinancier && (
                <Text.Subtitle2 className="text-grey-700">{org.name}</Text.Subtitle2>
              )}
            </div>
            <Tabs.Root value={tab} onValueChange={onChangeTab} orientation="horizontal">
              <Tabs.List aria-label="Settings tabs" className="m-0 w-fit border-none">
                <Tabs.Trigger value={SettingsTab.Financial}>
                  {SettingsTab.Financial}
                </Tabs.Trigger>
                <Tabs.Trigger value={SettingsTab.Users}>
                  {SettingsTab.Users}
                </Tabs.Trigger>
              </Tabs.List>
            </Tabs.Root>
          </div>
        </div>

        <PageContainer className="p-5">
          {match(tab)
            .with(SettingsTab.Financial, () => <OrgSettings {...{ org, refetch }} />)
            .with(SettingsTab.Users, () => <UserManagement {...{ org, portfolios }} />)
            .exhaustive()}
        </PageContainer>
      </div>
    </>
  )
}
